import type { InferOutput } from 'valibot';
import {
  any,
  boolean,
  email,
  intersect,
  literal,
  maxLength,
  minLength,
  object,
  optional,
  pipe,
  string,
  transform,
  trim,
  variant,
} from 'valibot';

const contactSchema = object({
  phoneNumber: pipe(string(), trim(), minLength(1, 'Validation.PhoneNumber.NotEmpty')),
  emailAddress: pipe(
    string(),
    minLength(1, 'Validation.EmailAddress.NotEmpty'),
    email('Validation.EmailAddress.InvalidFormat')
  ),
  billingStreet: pipe(string(), minLength(1, 'Validation.Street.NotEmpty')),
  billingTown: pipe(string(), minLength(1, 'Validation.Town.NotEmpty')),
  billingPostCode: pipe(string(), minLength(1, 'Validation.PostCode.NotEmpty')),
  billingCountryCode: pipe(string(), minLength(1, 'Validation.CountryCode.NotEmpty')),
});

const personSchema = object({
  titlePrefix: optional(string()),
  firstName: pipe(string(), minLength(1, 'Validation.FirstName.NotEmpty')),
  lastName: pipe(string(), minLength(1, 'Validation.FirstName.NotEmpty')),
  titleSuffix: optional(string()),
});

const companySchema = object({
  isCompany: literal(true),
  customerName: pipe(string(), minLength(1, 'Validation.CustomerName.NotEmpty')),
  registrationNumber: pipe(
    string(),
    minLength(1, 'Validation.RegistrationNumber.NotEmpty'),
    maxLength(18, 'IČO je príliš dlhé')
  ),
  vatNumber: string(),
  taxNumber: optional(string()),
  notVatRegistered: optional(pipe(any(), transform(Boolean), boolean())),
  ...personSchema.entries,
});

const citizenSchema = object({
  ...personSchema.entries,
  isCompany: literal(false),
});

const companyOrPersonSchema = variant('isCompany', [citizenSchema, companySchema]);

const shippingAddressSchema = variant('differentShipping', [
  object({
    differentShipping: literal(false),
    shippingAddress: optional(
      object({
        addressId: optional(string()),
        street: optional(string()),
        town: optional(string()),
        postCode: optional(string()),
        countryCode: optional(string()),
      })
    ),
  }),
  object({
    differentShipping: literal(true),
    shippingAddress: object({
      addressId: optional(string()),
      street: pipe(string(), minLength(1, 'Validation.Street.NotEmpty')),
      town: pipe(string(), minLength(1, 'Validation.Town.NotEmpty')),
      postCode: pipe(string(), minLength(1, 'Validation.PostCode.NotEmpty')),
      countryCode: pipe(string(), minLength(1, 'Validation.CountryCode.NotEmpty')),
    }),
  }),
]);

export const customerSchema = intersect([
  companyOrPersonSchema,
  contactSchema,
  shippingAddressSchema,
]);

export const CheckoutFormSchema = object({
  note: optional(string()),
  shippingMethodId: string(),
  paymentMethodId: string(),
  customer: customerSchema,
});

export type CheckoutFormSchema = InferOutput<typeof CheckoutFormSchema>;
